import React, { useEffect, useState } from 'react';
import { Permission, SwarmType } from '@/graphql/types';
import { firstValueFrom } from 'rxjs';
import { object, type AnySchema } from 'yup';
import { schema } from '../../../../../schema';
import { AddElementSlot } from '../../addElementSlot/AddElementSlot';
import { BaseReportElement } from '../BaseReportElement';
import { combineRenderableContent, renderDefinitionList } from '../renderHelpers';
import { getBindingCatalog } from '../../runtime/dataServices/bindingCatalog';
import { validateProjectMeasuringPointsResult } from '../../runtime/dataServices/projectMeasuringPoints/service';
import type {
    BuildStructureSettingsYupSchemaHelpers,
    ContextForKind,
    ElementDefinition,
    ElementInsertionSlotKind,
    ElementContentRenderResult,
    ElementSettings,
    MeasuringPointRenderContext,
    MeasuringPointModel,
    RenderRuntime,
} from '../types';
import type { StructurePath } from '../structurePath';

interface RepeatSettings extends ElementSettings {
    children: ElementDefinition[];
}

interface RenderedRepeatResult {
    nodes: React.ReactNode[];
    ready: Promise<void>;
    hasRenderableContent: Promise<boolean>;
}

interface RepeatRendererProps {
    renderedRepeatPromise: Promise<RenderedRepeatResult>;
}

function createMeasuringPointRenderContext(
    measuringPoint: MeasuringPointModel
): MeasuringPointRenderContext {
    return {
        kind: 'measuring-point',
        measuringPoint,
    };
}

function createDemoMeasuringPointRenderContext(
    projectContext: ContextForKind<'project'>
): MeasuringPointRenderContext {
    const demoMeasuringPointId = schema.demoMeasuringPoints.vibration;
    if (!demoMeasuringPointId) {
        throw new Error(
            'No demo vibration measuring point configured in schema.demoMeasuringPoints.'
        );
    }

    return createMeasuringPointRenderContext({
        id: String(demoMeasuringPointId),
        name: `${projectContext.project.name} demo measuring point`,
        deviceName: null,
        swarmType: SwarmType.Vibration,
        permission: Permission.View,
    });
}

function getRepeatChildInsertionSlotKind(runtime: RenderRuntime): ElementInsertionSlotKind {
    const location = runtime.getCurrentElementLocation();
    if (!location) {
        throw new Error('Missing repeat element location while resolving insertion slot kind.');
    }

    // Repeat does not introduce a semantic container boundary. At template root,
    // its children should still behave like template-root insertions; nested
    // repeats keep normal child-list semantics.
    return location.listPath.length === 0 ? 'template-root' : 'child-list';
}

function renderRepeatedMeasuringPointNodes(
    settings: RepeatSettings,
    runtime: RenderRuntime,
    childrenPath: StructurePath,
    childInsertionSlotKind: ElementInsertionSlotKind,
    measuringPoints: readonly MeasuringPointModel[],
    demoMeasuringPointContext: MeasuringPointRenderContext
): RenderedRepeatResult {
    const childReadyPromises: Promise<void>[] = [];
    const childContentResults: Promise<boolean>[] = [];
    const measuringPointContexts = measuringPoints.map(createMeasuringPointRenderContext);

    const repeatedNodes = measuringPointContexts.map((measuringPointCtx, index) => {
        const rendered = renderDefinitionList(
            settings.children,
            measuringPointCtx,
            runtime,
            childrenPath,
            childInsertionSlotKind
        );
        childReadyPromises.push(rendered.ready);
        childContentResults.push(rendered.hasRenderableContent);

        return (
            <React.Fragment key={`repeat-${measuringPointCtx.measuringPoint.id}-${index}`}>
                {rendered.nodes}
            </React.Fragment>
        );
    });
    const insertionRenderContext = measuringPointContexts[0] ?? demoMeasuringPointContext;
    const addElementSlotNode = runtime.showEditor ? (
        <AddElementSlot
            runtime={runtime}
            listPath={childrenPath}
            contextKind="measuring-point"
            slotKind={childInsertionSlotKind}
            buttonLabel={gettext('ADD_ELEMENT')}
            renderContext={insertionRenderContext}
        />
    ) : null;

    return {
        nodes: [...repeatedNodes, addElementSlotNode],
        ready: Promise.all(childReadyPromises).then(() => undefined),
        // With no repeated instances there is no rendered content. This differs from
        // an intentionally empty definition list, which the shared aggregator keeps
        // visible for authoring.
        hasRenderableContent:
            measuringPointContexts.length === 0
                ? Promise.resolve(false)
                : combineRenderableContent(childContentResults),
    };
}

function RepeatRenderer({ renderedRepeatPromise }: RepeatRendererProps): JSX.Element {
    const [nodes, setNodes] = useState<React.ReactNode[] | null>(null);
    const [renderError, setRenderError] = useState<Error | null>(null);

    useEffect(() => {
        let isActive = true;
        setNodes(null);
        setRenderError(null);

        void renderedRepeatPromise
            .then((renderedRepeat) => {
                if (!isActive) {
                    return;
                }

                setNodes(renderedRepeat.nodes);
            })
            .catch((error) => {
                if (!isActive) {
                    return;
                }

                setRenderError(error instanceof Error ? error : new Error(String(error)));
            });

        return () => {
            isActive = false;
        };
    }, [renderedRepeatPromise]);

    if (renderError) {
        throw renderError;
    }

    return <>{nodes}</>;
}

function renderRepeatElement(
    settings: RepeatSettings,
    projectContext: ContextForKind<'project'>,
    runtime: RenderRuntime,
    childrenPath: StructurePath
): ElementContentRenderResult {
    const childInsertionSlotKind = getRepeatChildInsertionSlotKind(runtime);
    const demoMeasuringPointContext = createDemoMeasuringPointRenderContext(projectContext);
    const renderedRepeatPromise = firstValueFrom(
        runtime.observeBinding(
            getBindingCatalog().toActiveDataBinding(
                'project.measuring_points.measuring_points',
                projectContext.project.id
            )
        )
    )
        .then((linkValue) => validateProjectMeasuringPointsResult(linkValue))
        .then((measuringPoints) =>
            renderRepeatedMeasuringPointNodes(
                settings,
                runtime,
                childrenPath,
                childInsertionSlotKind,
                measuringPoints,
                demoMeasuringPointContext
            )
        );

    const hasRenderableContent = renderedRepeatPromise.then(
        (renderedRepeat) => renderedRepeat.hasRenderableContent
    );

    return {
        node: <RepeatRenderer renderedRepeatPromise={renderedRepeatPromise} />,
        ready: Promise.all([
            renderedRepeatPromise.then((renderedRepeat) => renderedRepeat.ready),
            hasRenderableContent,
        ]).then(() => undefined),
        hasRenderableContent,
    };
}

export class RepeatElement extends BaseReportElement<'project', RepeatSettings> {
    static readonly elementType = 'repeat';

    static readonly layout = {
        flow: 'derived',
        getChildDefinitionLists: (settings: ElementSettings) => [
            (settings as RepeatSettings).children,
        ],
    } as const;

    static readonly compatibleContextKinds = ['project'] as const;

    static get insertion() {
        return {
            label: gettext('ELEMENT_REPEAT_PROJECT_MEASURING_POINTS_LABEL'),
            supportedSlots: ['template-root', 'child-list'] as const,
            createDefinition: () => ({
                type: 'repeat',
                settings: { children: [] },
            }),
        };
    }

    static buildStructureSettingsYupSchema({
        buildRequiredDefinitionsSchema,
    }: BuildStructureSettingsYupSchemaHelpers): AnySchema {
        return object({
            children: buildRequiredDefinitionsSchema(),
        });
    }

    protected toCompatibleReactNode(
        ctx: ContextForKind<'project'>,
        runtime: RenderRuntime
    ): ElementContentRenderResult {
        return renderRepeatElement(
            this.settings,
            ctx,
            runtime,
            runtime.getCurrentElementChildListPath('children')
        );
    }
}
